import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DeleteIcon from "src/assets/all-icons/delete-icon.jsx";

import ShareIcon from '@/assets/all-icons/share-icon';
import useGoBack from '@/hooks/useGoBack';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import EditArtlistIcon from '@/shared-components/icons/components/actions/EditArtlistIcon';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import ThreeDotsIcon from '@/shared-components/icons/components/public/ThreeDotsIcon';
import { buildPublicContentPath, resolvePublicContentKind } from '@/utils/contentRoutes';
import { canAccessGalleryPermission } from '@/utils/galleryPermissions';
import { handleShareFailure } from '@/utils/share.utils';

import DeletePost from './components/DeletePost';
import HeaderOption from './components/HeaderOption';

import './index.css';

export default function Header({ fetchData }) {
  const t = useTranslations('common.content');
  const { state, stateHandler } = useStateHandler();
  // pick whichever exists
  const owner = fetchData.gallery || fetchData.artist || fetchData.collector;
  const ownerUsername = owner?.user_username || state.user_session?.user_username;

  // figure out whether this is an article or a post,
  // and go back to the matching tab
  const contentKind = resolvePublicContentKind(fetchData);
  const isArticle = contentKind === 'article';
  const goBackHandler = useGoBack(
    ownerUsername
      ? `/user/${ownerUsername}/board?view=${isArticle ? 'articles' : 'posts'}`
      : `/profile/board?view=${isArticle ? 'articles' : 'posts'}`
  );

  const [openHeaderOptions, setOpenHeaderOptions] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const location = useLocation();
  // const goBackHandler = () => navigate(-1); (not sure if needed)

  // If your auth is still loading, just render the BackArrow (or a spinner)
  if (state.user_session === undefined) {
    return (
      <nav className="post-header__container">
        <BackArrowIcon className="post-header__go-back" onClick={() => navigate(-1)} />
      </nav>
    );
  }

  // a plain -1 POP will restore the state we replaced above
  function handleGoBack() {
    // use React Router to go back one entry, enabling ScrollRestoration
    goBackHandler();
  }

  function sharePost() {
    if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
      return;
    }

    navigator
      .share({
        title: fetchData.post_container.post_title,
        text: t('share_text', { title: fetchData.post_container.post_title }),
        url: `${window.location.origin}${buildPublicContentPath(fetchData, contentKind)}`,
      })
      .catch((error) => handleShareFailure(error, stateHandler, 'Post share'));
  }

  function canEditPost() {
    const session = state.user_session;
    if (!session) {
      return false;
    }

    if (owner?._id && owner._id === session?._id) {
      return true;
    }

    const galleryId = fetchData.gallery?._id || fetchData.gallery;
    if (!galleryId) {
      return false;
    }

    const requiredPermission = isArticle ? 'board.article' : 'board.post';
    return canAccessGalleryPermission(session, requiredPermission, String(galleryId));
  }

  function canDeletePost() {
    const session = state.user_session;
    if (!session) {
      return false;
    }

    if (owner?._id && owner._id === session?._id) {
      return true;
    }

    const galleryId = fetchData.gallery?._id || fetchData.gallery;
    if (!galleryId) {
      return false;
    }

    const requiredPermission = isArticle ? 'board.article_delete' : 'board.post_delete';
    return canAccessGalleryPermission(session, requiredPermission, String(galleryId));
  }

  const canEdit = canEditPost();
  const canDelete = canDeletePost();
  const canManage = canEdit || canDelete;

  return (
    <>
      <nav className="post-header__container">
        <BackArrowIcon className="post-header__go-back" onClick={handleGoBack}/> 
        {/*  or goBackHandler (not sure) */}
        {
          canManage ? 
            <div 
              tabIndex={0} 
              onClick={()=>setOpenHeaderOptions(!openHeaderOptions)} 
              onBlur={()=>setOpenHeaderOptions(false)} 
              className="post-header__threedots-container"
              aria-label={t('more_options')}
              aria-expanded={openHeaderOptions}
            >
              <ThreeDotsIcon className="post-header__threedots-icon"/>

              <div className={`element-non-selectable post-header__options${openHeaderOptions ? " active" : ""}`}>
                {canEdit && (
                  <HeaderOption
                    text={isArticle ? t('edit_article') : t('edit_post')}
                    icon={<EditArtlistIcon className="post-header__option-icon edit"/>}
                    onClick={() => {
                      if (isArticle) {
                        navigate(`/profile/edit-article/${fetchData._id}`);
                      } else {
                        // for posts we pass the data via state
                        navigate(`/profile/edit-post/${fetchData._id}`, { state: { postData: fetchData } });
                      }
                    }}
                  />
                )}

                {canDelete && (
                  <HeaderOption
                    text={isArticle ? t('delete_article') : t('delete_post')}
                    icon={<DeleteIcon className="post-header__option-delete"/>}
                    onClick={()=>setOpenDeleteDialog(true)}
                  />
                )}

                {navigator.share && (
                  <HeaderOption
                    text={t('share')}
                    icon={<ShareIcon className="post-header__option-icon"/>}
                    onClick={sharePost}
                  />
                )}
              </div>
            </div>
            :
            navigator.share && <ShareIcon className="post-header__share-icon" onClick={sharePost}/>
        }
      </nav>

      <DeletePost 
        open={openDeleteDialog} 
        close={()=>setOpenDeleteDialog(false)} 
        fetchData={fetchData}
        mode={isArticle ? 'article' : 'post'}
      />
    </>
  );
}
